import { useEffect, useState } from "react";
import { ScrollArea, ScrollBar } from "@/shadcnComponents/ui/scroll-area";
import {
  CircleCheck,
  Eye,
  Info,
  Laptop,
  Repeat2,
  Send,
  Image,
  Smartphone,
  Loader,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/shadcnComponents/ui/button";
import { Textarea } from "../../../../../shadcnComponents/ui/textarea";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../shadcnComponents/ui/tooltip";
import { BrandAlertDialog } from "../Components/Components";
import { handlePreview } from "../Helpers/Helper";
import { convertToIST } from "@/Helpers/times-ago";

// types ----------------------
interface InitialEditorProps {
  content?: string;
  dummyText?: string;
  influencerName?: string;
  tagline?: string;
  profileImage?: string;
  images?: File[];
  videos?: File[];
  viewMode?: "desktop" | "mobile";
  docid?: string;
  editorMode?: "edit" | "view";
  status?: 0 | 1 | 2 | 3;
  draftStatus?: string;
  suggestionComment?: string;
  suggestionImages?: File[];
  influencerId?: string;
  isApproved?: boolean;
  submittedAt?: string;
  suggestedAt?: string;
  approvedAt?: string;
}

// components ----------------------

interface HeaderProps {
  title: string;
  description?: string;
}

const Header = ({ title, description }: HeaderProps) => {
  return (
    <div className="text-center space-y-2">
      <h1 className="text-[#FFF] font-[Inter] text-[20px] not-italic font-bold leading-[24px]">
        {title}
      </h1>
      {description && (
        <p className="text-[#9E9E9E] text-center font-[Inter] text-[14px] not-italic font-normal leading-[20px]">
          {description}
        </p>
      )}
    </div>
  );
};

interface ToolbarProps {
  setViewMode: (mode: string) => void;
  viewMode: string;
  draftStatus: string;
  submittedAt?: string;
}

const Toolbar = ({
  setViewMode,
  viewMode,
  draftStatus,
  submittedAt,
}: ToolbarProps) => {
  return (
    <div className="relative w-full flex items-center justify-between">
      <div className="flex gap-2 items-center justify-center">
        <span className="text-[#000] font-[Inter] text-[14px] not-italic font-semibold leading-[24px]">
          Post Preview
        </span>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info size={19} color="#666666" />
            </TooltipTrigger>
            <TooltipContent className="bg-white border ">
              <p className="w-[300px] text-xs text-center">
                Toggle between mobile and desktop views to see how your content
                would appear on LinkedIn
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {submittedAt && (
          <div className="text-[#9E9E9E] font-[Inter] text-[12px] not-italic font-normal leading-[20px] p-4">
            Submitted on: {convertToIST(submittedAt)}
          </div>
        )}
      </div>
      <div className="px-2 py-1 bg-[#FFF] rounded-md flex items-center justify-center">
        {[
          {
            icon: <Smartphone size={20} />,
            mode: "mobile",
          },
          {
            icon: <Laptop size={20} />,
            mode: "desktop",
          },
        ]?.map((item, i) => {
          return (
            <button
              key={i}
              className={cn(
                "text-[#A3A3A3] p-2 rounded-md hover:text-[##121212]",
                viewMode === item?.mode && "bg-[#F8F6F6] text-[#000]"
              )}
              onClick={() => setViewMode(item?.mode)}
            >
              {item?.icon}
            </button>
          );
        })}
      </div>
      {draftStatus && (
        <div className="">
          <StatusBadge variant="NEW_DRAFT" value={draftStatus} />
        </div>
      )}
    </div>
  );
};

interface AddImageAreaProps {
  editorState: InitialEditorProps;
}

const AddImageArea = ({ editorState }: AddImageAreaProps) => {
  return (
    <div className="w-full">
      <div className="text-[#121212] font-[Inter] text-[14px] not-italic font-semibold leading-[22px]">
        Image(s) or Video(s) to be added
      </div>

      <ScrollArea className="py-2">
        <div className="flex gap-2">
          {editorState?.images?.length > 0 &&
            editorState?.images?.map((file, index) => (
              <div
                key={index}
                className="relative m-2 shrink-0 flex flex-col gap-2"
              >
                <div key={index} className="relative group">
                  <img
                    className="w-20 h-20 object-cover rounded-md"
                    src={
                      file instanceof Blob ? URL.createObjectURL(file) : file
                    }
                    alt={`preview-${index}`}
                  />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-lg">
                    <Button
                      onClick={(e) => {
                        e.preventDefault();
                        handlePreview(file);
                      }}
                      variant="outline"
                      className="text-white bg-transparent hover:bg-transparent border-none flex items-center gap-2 text-xs px-3 py-1 h-8"
                    >
                      <Eye size={10} />
                      Preview
                    </Button>
                  </div>
                </div>

                {!(file instanceof Blob) && (
                  <span className="text-xs">
                    {(file as string)?.split("-")?.pop()}
                  </span>
                )}
              </div>
            ))}
        </div>
        {editorState?.videos?.length > 0 &&
          editorState?.videos?.map((file, index) => (
            <div
              key={index}
              className="relative m-2 shrink-0 flex flex-col gap-2"
            >
              <div className="w-full h-full flex justify-center items-center">
                <video
                  className="rounded-md shadow-md w-3/4 "
                  controls
                  autoPlay={false}
                  src={file instanceof Blob ? URL.createObjectURL(file) : file}
                />
              </div>
              {!(file instanceof Blob) && (
                <span className="text-xs">
                  {(file as string)?.split("-")?.pop()}
                </span>
              )}
            </div>
          ))}
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
};

interface PreviewProps {
  content: string;
  viewMode: "mobile" | "desktop";
  currentUserName?: string;
  profileImage?: string;
  tagline?: string;
  likes?: number;
  date?: string;
  comments?: number;
  reposts?: number;
  impressions?: number;
}

export const Preview = ({
  content,
  viewMode,
  currentUserName,
  profileImage,
  tagline,
  likes,
  date,
}: PreviewProps) => {
  const [seeMore, setSeeMore] = useState(false);

  return (
    <div
      className={cn(
        "w-full rounded-lg bg-[#fff] flex flex-col p-6 gap-2",
        `${
          viewMode === "mobile"
            ? "w-[390px]"
            : viewMode === "desktop"
            ? "w-[555px]"
            : ""
        }`
      )}
    >
      <div className="flex items-center gap-3">
        {!profileImage ? (
          <div className="size-[50px] rounded-full bg-[#AFB1B2] shrink-0"></div>
        ) : (
          <img
            src={profileImage}
            alt="image"
            className="size-[50px] shrink-0 object-cover rounded-full"
          />
        )}
        <div className="w-full">
          <h1 className="text-[#121212] font-[Inter] text-[14px] not-italic font-semibold leading-[24px] line-clamp-1">
            {currentUserName || "Your Name"}
          </h1>
          <p className="text-[#64748B] font-[Inter] text-[12px] not-italic font-normal leading-[normal] line-clamp-1 w-2/3">
            {tagline || "Your tagline"}
          </p>
          <p className="text-[#64748B] font-[Inter] text-[12px] not-italic font-normal leading-[normal] line-clamp-1 w-2/3 mt-1">
            {date || "1h"}
          </p>
        </div>
      </div>
      <div className="pb-3">
        <p>
          <span
            className={cn(
              "text-[#54575B] font-inter text-[12px] not-italic font-normal leading-[22px] relative overflow-hidden",
              seeMore ? "line-clamp-none" : "line-clamp-3"
            )}
            style={{
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              whiteSpace: "pre-wrap",
            }}
          >
            {content}
          </span>
          <div
            className="text-[#64748B] font-[Inter] text-[12px] not-italic font-normal leading-[22px] hover:underline bg-[#fff] cursor-pointer w-full text-right"
            onClick={() => setSeeMore(!seeMore)}
          >
            {seeMore ? "...see less" : "...see more"}
          </div>
        </p>
      </div>
      <div className="flex gap-2">
        <img
          src="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/1727856335032-Frame_1430102479_%281%29.png"
          alt="reactions"
          className="h-4"
        />
        <span className="text-[#949698] font-[Inter] text-[12px] not-italic font-bold leading-[normal]">
          {likes ? `${likes} Likes` : "Aesha and 76 others"}
        </span>
      </div>
      <div className="flex gap-5 items-center pt-3">
        {!profileImage ? (
          <div className="size-[30px] rounded-full bg-[#AFB1B2] shrink-0"></div>
        ) : (
          <img
            src={profileImage}
            alt="image"
            className="size-[30px] shrink-0 object-cover rounded-full"
          />
        )}
        {[
          {
            icon: (
              <img
                src="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/1727853788915-ei_like.png"
                alt="like"
                className="w-[30px] h-[30px]"
              />
            ),
            title: "Like",
          },
          {
            icon: (
              <img
                src="https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/1727855749998-lets-icons_comment-light.png"
                alt="comment"
                className="w-[30px] h-[30px]"
              />
            ),
            title: "Comment",
          },
          {
            icon: <Repeat2 size={24} color="#AFB1B2" />,
            title: "Repost",
          },
          {
            icon: <Send size={19} color="#AFB1B2" />,
            title: "Share",
          },
        ]?.map((item, i) => {
          return (
            <div key={i} className="flex gap-1 items-center justify-center">
              {item?.icon}
              {!(viewMode === "mobile") && (
                <span className="text-[#949698] font-inter text-[12px] flex items-center justify-center">
                  {item?.title}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface SuggestionAreaProps {
  onSubmitSuggestion: ({
    e,
    comment,
    editorState,
    images,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    e?: any;
    comment?: string;
    editorState?: InitialEditorProps;
    images?: File[];
    isApprove?: boolean;
  }) => Promise<boolean>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onApproveDraft: ({ e }: { e?: any }) => Promise<void>;
  suggestionComment: string;
  suggestionImages: File[];
  editorState: InitialEditorProps;
  suggestedAt?: string;
}

const SuggestionArea = ({
  onSubmitSuggestion,
  onApproveDraft,
  suggestionComment,
  suggestionImages,
  editorState,
  suggestedAt,
}: SuggestionAreaProps) => {
  const [comment, setComment] = useState<string>("");
  const [images, setImages] = useState<File[]>([]);
  const [openSuggestionInput, setOpenSuggestionInput] =
    useState<boolean>(false);
  const [seeMore, setSeeMore] = useState(false);
  const [isSuggestionAndApprove, setIsSuggestionAndApprove] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setImages((prev) => [...Array.from(e.target.files), ...prev]);
    }
  };

  const handleImageRemove = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };
  const handleAfterSubmission = async (result: boolean) => {
    if (result) {
      setComment("");
      setImages([]);
      setOpenSuggestionInput(false);
      setIsSubmitting(false);
    }
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    if (isSuggestionAndApprove) {
      setIsSubmitting(true);
      const result = await onSubmitSuggestion({
        e,
        comment,
        editorState,
        images,
        isApprove: true,
      });
      handleAfterSubmission(result);
    } else {
      e.preventDefault();
      setIsSubmitting(true);
      const result = await onSubmitSuggestion({
        e,
        comment,
        editorState,
        images,
      });
      handleAfterSubmission(result);
    }
  };

  useEffect(() => {
    if (suggestionComment?.trim()?.length > 0) {
      setComment(suggestionComment);
    }
  }, [suggestionComment]);
  useEffect(() => {
    if (suggestionImages?.length > 0) {
      setImages(suggestionImages);
    }
  }, [suggestionImages]);

  const handleGiveSuggestion = () => {
    if (openSuggestionInput) {
      setOpenSuggestionInput(false);
      setIsSuggestionAndApprove(false);
    } else {
      setOpenSuggestionInput(true);
    }
  };
  const handleSuggestionAndApprove = () => {
    setOpenSuggestionInput(true);
    setIsSuggestionAndApprove(true);
  };
  return (
    <div className="w-full">
      {suggestionComment?.length > 0 && (
        <div className="space-y-2">
          <div className="flex flex-col">
            <span className="text-[#121212] font-[Inter] text-[14px] not-italic font-semibold leading-[normal]">
              Changes Suggested
            </span>
            <span
              className={cn(
                "text-[#64748B] font-[Inter] text-[12px] not-italic font-normal leading-[22px] whitespace-pre-wrap pr-6 pt-2 break-words max-w-lg",
                seeMore ? "line-clamp-none" : "line-clamp-6"
              )}
              onClick={() => setSeeMore(!seeMore)}
            >
              {suggestionComment}
              <div className="flex gap-2">
                {suggestionImages?.length > 0 &&
                  suggestionImages?.map((file, index) => (
                    <div
                      key={index}
                      className="relative m-2 shrink-0 flex flex-col gap-2"
                    >
                      <div key={index} className="relative group">
                        <img
                          className="w-20 h-20 object-cover rounded-md"
                          src={
                            file instanceof Blob
                              ? URL.createObjectURL(file)
                              : file
                          }
                          alt={`preview-${index}`}
                        />
                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-lg">
                          <Button
                            onClick={(e) => {
                              e.preventDefault();
                              handlePreview(file);
                            }}
                            variant="outline"
                            className="text-white bg-transparent hover:bg-transparent border-none flex items-center gap-2 text-xs px-3 py-1 h-8"
                          >
                            <Eye size={10} />
                            Preview
                          </Button>
                        </div>
                      </div>

                      {!(file instanceof Blob) && (
                        <span className="text-xs">
                          {(file as string)?.split("-")?.pop()}
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            </span>

            {suggestedAt && (
              <div className="text-[#9E9E9E] font-[Inter] text-[12px] not-italic font-normal leading-[20px] mt-2">
                Suggested on: {convertToIST(suggestedAt)}
              </div>
            )}
          </div>
        </div>
      )}

      {!(suggestionComment?.length > 0) && !editorState?.isApproved && (
        <div className="mt-4 mb-7 flex gap-4">
          <Button variant="outline" onClick={() => handleGiveSuggestion()}>
            {openSuggestionInput ? "Remove Suggestion" : "Suggest Changes"}
          </Button>
          <BrandAlertDialog
            trigger={
              <Button variant="destructive" disabled={openSuggestionInput}>
                Approve Post
              </Button>
            }
            heading="Are you sure?"
            desc="approving this draft is permanent and cannot be undone. Please confirm if you want to proceed with approving this draft."
            onContinue={(e) => onApproveDraft({ e })}
          />
          <Button
            variant="destructive"
            onClick={() => handleSuggestionAndApprove()}
            className="hidden"
          >
            Suggest Changes and Approve
          </Button>
        </div>
      )}
      {!(suggestionComment?.length > 0) && openSuggestionInput && (
        <div className="space-y-2">
          <div className="flex flex-col">
            <span className="text-[#121212] font-[Inter] text-[14px] not-italic font-semibold leading-[normal]">
              Give Suggestions
            </span>
            <span className="text-[#898888] font-[Inter] text-[12px] not-italic font-normal leading-[normal]">
              From content to its alignment, you can request any changes to be
              made
            </span>
          </div>
          <div className="space-y-5">
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Write your suggestion here..."
              className="w-full h-[175px] text-[#424242] font-[Inter] text-[14px] not-italic font-normal leading-[24px]"
            />
            <ScrollArea className="py-2">
              <div className="flex gap-2">
                <div className="flex items-center justify-center my-2 mr-2 size-20  shrink-0">
                  <input
                    id="file-upload"
                    className="hidden"
                    type="file"
                    multiple
                    accept=".png, .jpg"
                    onChange={(e) => handleImageChange(e)}
                  />
                  <label
                    htmlFor="file-upload"
                    className="flex justify-center items-center border-2 border-dashed border-gray-400 rounded-md cursor-pointer flex-col gap-1 py-2 px-1 text-center shrink-0 size-full"
                  >
                    <Image size={20} color="#666666" className="shrink-0" />
                    <span className="text-[#121212] font-[Inter] text-[10px] not-italic font-normal leading-[22px]">
                      Select Image
                    </span>
                  </label>
                </div>
                {images?.map((file, index) => (
                  <div
                    key={index}
                    className="relative m-2 shrink-0 flex flex-col gap-2 "
                  >
                    <div key={index} className="relative group">
                      <img
                        className="w-20 h-20 object-cover rounded-md"
                        src={
                          file instanceof Blob
                            ? URL.createObjectURL(file)
                            : file
                        }
                        alt={`preview-${index}`}
                      />
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/30 rounded-lg">
                        <Button
                          onClick={(e) => {
                            e.preventDefault();
                            handlePreview(file);
                          }}
                          variant="outline"
                          className="text-white bg-transparent hover:bg-transparent border-none flex items-center gap-2 text-xs px-3 py-1 h-8"
                        >
                          <Eye size={10} />
                          Preview
                        </Button>
                      </div>
                    </div>
                    {!(file instanceof Blob) && (
                      <span className="text-xs">
                        {(file as string)?.split("-")?.pop()}
                      </span>
                    )}
                    {editorState?.editorMode === "edit" && (
                      <div
                        onClick={() => handleImageRemove(index)}
                        className="w-4 h-4 flex items-center justify-center text-sm absolute top-1 right-1 bg-red-500 text-white rounded-full cursor-pointer"
                      >
                        &times;
                      </div>
                    )}
                    {/* Preview Button */}
                  </div>
                ))}
              </div>

              <ScrollBar orientation="horizontal" />
            </ScrollArea>
            {isSuggestionAndApprove ? (
              <BrandAlertDialog
                trigger={
                  <Button
                    variant="destructive"
                    disabled={!comment || isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader className=" animate-spin h-4 w-4 " />
                    ) : (
                      "Approve Post"
                    )}
                  </Button>
                }
                heading="Are you sure?"
                desc="approving this draft is permanent and cannot be undone. Please confirm if you want to proceed with approving this draft."
                onContinue={(e) => handleSubmit(e)}
              />
            ) : (
              <Button
                variant="destructive"
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                onClick={async (e: any) => {
                  handleSubmit(e);
                }}
                className="px-10"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <Loader className=" animate-spin h-4 w-4 " />
                ) : isSuggestionAndApprove ? (
                  "Approve"
                ) : (
                  "Submit"
                )}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface StatusBadgeProps {
  variant: "NEW_DRAFT";
  value: string;
}

const StatusBadge = ({ variant, value }: StatusBadgeProps) => {
  return (
    <div
      className={cn(
        "py-1 px-4 font-[Inter] text-[12px] not-italic font-semibold leading-[24px] rounded-[6px] uppercase",
        {
          NEW_DRAFT: "bg-[#36D7CC33]/20 text-[#115E59]",
        }[variant]
      )}
    >
      {value}
    </div>
  );
};

// LinkedinPostEditor ----------------------

interface LinkedinPostEditorProps {
  initial?: InitialEditorProps;
  header?: HeaderProps;
  onApproveDraft?: SuggestionAreaProps["onApproveDraft"];
  onSubmitSuggestion?: SuggestionAreaProps["onSubmitSuggestion"];
}

export const LinkedinPostEditor = ({
  initial = {},
  header,
  onApproveDraft,
  onSubmitSuggestion,
}: LinkedinPostEditorProps) => {
  const [editorState, setEditorState] = useState<InitialEditorProps>({
    content: "",
    dummyText: `Start writing and your post will appear here..\n\nYou can add images, links, #hashtags and emojis 🤩`,
    influencerName: "",
    tagline: "",
    profileImage: "",
    images: [],
    videos: [],
    viewMode: "desktop",
    docid: "",
    editorMode: "edit",
    status: 3,
    draftStatus: "NEW DRAFT",
    suggestionComment: "",
    suggestionImages: [],
    influencerId: "",
    isApproved: false,
  });

  useEffect(() => {
    setEditorState((prev) => ({
      ...prev,
      ...initial,
    }));
  }, [initial]);

  const handleViewModeChange = (mode: InitialEditorProps["viewMode"]) => {
    setEditorState((prev) => ({
      ...prev,
      viewMode: mode,
    }));
  };
  const handleSubmitSuggestion = async ({
    e,
    comment,
    editorState,
    images,
    isApprove,
  }: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    e?: any;
    comment?: string;
    editorState?: InitialEditorProps;
    images?: File[];
    isApprove?: boolean;
  }) => {
    const result = await onSubmitSuggestion({
      e,
      comment,
      images,
      editorState,
      isApprove,
    });
    setEditorState((prev) => {
      return {
        ...prev,
        suggestionComment: "",
        suggestionImages: [],
      };
    });

    return result;
  };

  return (
    <>
      <div className="flex flex-col gap-5">
        {header && (
          <Header title={header?.title} description={header?.description} />
        )}
        <div className="flex flex-col">
          <div className="bg-[#F8F6F6] px-7 py-2 rounded-t-lg">
            <Toolbar
              setViewMode={handleViewModeChange}
              viewMode={editorState?.viewMode}
              draftStatus={editorState?.draftStatus}
              submittedAt={editorState?.submittedAt}
            />
          </div>
          <div className="flex h-full">
            <div className="min-w-1/2 max-w-7/12 flex-1 flex flex-col border-[#262626] rounded-l-lg rounded-tl-none bg-[#F8F6F6] items-center">
              <ScrollArea className="p-6">
                <Preview
                  content={editorState?.content}
                  viewMode={editorState?.viewMode}
                  currentUserName={editorState?.influencerName}
                  profileImage={editorState?.profileImage}
                  tagline={editorState?.tagline}
                />
              </ScrollArea>
            </div>
            <div className="w-1/2 flex flex-col rounded-r-lg rounded-t-none bg-[#F8F6F6] p-5">
              {(editorState?.images?.length > 0 ||
                editorState?.videos?.length > 0) && (
                <AddImageArea editorState={editorState} />
              )}

              <SuggestionArea
                onApproveDraft={onApproveDraft}
                onSubmitSuggestion={async ({
                  e,
                  comment,
                  images,
                  isApprove,
                  editorState,
                }) => {
                  const result = await handleSubmitSuggestion({
                    e,
                    comment,
                    images,
                    isApprove,
                    editorState,
                  });
                  return result;
                }}
                suggestionComment={editorState?.suggestionComment}
                suggestionImages={editorState?.suggestionImages}
                editorState={editorState}
                suggestedAt={editorState?.suggestedAt}
              />

              {editorState?.isApproved && (
                <div
                  className={
                    "py-1 px-4 font-[Inter] text-[12px] not-italic font-semibold leading-[24px] rounded-[6px] uppercase bg-[#36D7CC33]/20 text-[#115E59] flex gap-2 items-center w-fit"
                  }
                >
                  <CircleCheck fill="#115E59" color="#fff" />
                  APPROVED DRAFT FOR CAMPAIGN
                </div>
              )}
              {/* show approved time */}
              {editorState?.isApproved && editorState?.approvedAt && (
                <div className="text-[#9E9E9E] font-[Inter] text-[12px] not-italic font-normal leading-[20px] p-4">
                  Approved on: {convertToIST(editorState?.approvedAt)}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
